import { useState, type FormEvent } from "react";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { useSuperAdminAuth } from "./SuperAdminAuthProvider";
import { useT } from "../i18n";

export function SuperAdminLogin() {
  const { status, signInWithEmail } = useSuperAdminAuth();
  const t = useT();
  const [email, setEmail] = useState("");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    await signInWithEmail(email.trim().toLowerCase());
  };

  const isSubmitting = status.kind === "loading";
  const isSent = status.kind === "magicLinkSent";
  const error = status.kind === "error" ? status.message : null;

  return (
    <div
      className="min-h-dvh grid place-items-center px-4"
      style={{ background: "var(--bg)" }}
    >
      <div
        className="w-full max-w-md p-8"
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        <div className="mb-7 text-center">
          <div
            className="mx-auto mb-3 grid h-14 w-14 place-items-center text-2xl font-black"
            style={{
              background: "var(--gradient-primary)",
              color: "var(--primary-on)",
              borderRadius: "var(--radius-lg)",
              fontFamily: "var(--font-sans)",
            }}
          >
            ⚡
          </div>
          <h1
            className="mb-1 text-2xl font-black"
            style={{ color: "var(--fg)", fontFamily: "var(--font-display)" }}
          >
            {t.brand.title}
          </h1>
          <p className="text-sm" style={{ color: "var(--fg-soft)" }}>
            {t.brand.subtitle}
          </p>
        </div>

        {isSent ? (
          <div className="text-center">
            <CheckCircle2
              className="mx-auto mb-3 h-12 w-12"
              style={{ color: "var(--ok)" }}
              aria-hidden
            />
            <h2 className="mb-2 text-lg font-extrabold" style={{ color: "var(--fg)" }}>
              {t.login.sent}
            </h2>
            <p
              className="mb-1 text-sm font-mono"
              style={{ color: "var(--primary)" }}
            >
              {status.kind === "magicLinkSent" ? status.email : ""}
            </p>
            <p className="text-sm" style={{ color: "var(--fg-soft)" }}>
              {t.login.sentHint}
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <h2
              className="mb-1 text-lg font-extrabold"
              style={{ color: "var(--fg)" }}
            >
              {t.login.title}
            </h2>
            <p className="mb-5 text-sm" style={{ color: "var(--fg-soft)" }}>
              {t.login.subtitle}
            </p>

            <label
              className="mb-1.5 block text-xs font-bold uppercase tracking-wide"
              style={{ color: "var(--fg-soft)" }}
            >
              {t.login.emailLabel}
            </label>
            <div className="relative mb-4">
              <Mail
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                style={{ color: "var(--fg-muted)" }}
                aria-hidden
              />
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                disabled={isSubmitting}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.login.emailPlaceholder}
                className="w-full pl-9 pr-3 py-2.5 text-sm"
                style={{
                  background: "var(--bg)",
                  color: "var(--fg)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                }}
              />
            </div>

            {error ? (
              <p
                className="mb-3 rounded p-2 text-xs"
                style={{
                  background: "var(--warn-bg)",
                  color: "var(--warn)",
                  border: "1px solid var(--warn)",
                }}
              >
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={isSubmitting || !email.trim()}
              className="flex w-full items-center justify-center gap-2 py-2.5 text-sm font-extrabold uppercase tracking-wide"
              style={{
                background: "var(--gradient-primary)",
                color: "var(--primary-on)",
                border: "none",
                borderRadius: "var(--radius-md)",
                cursor: isSubmitting ? "wait" : "pointer",
                opacity: !email.trim() ? 0.5 : 1,
              }}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner" />
                  {t.login.submitting}
                </>
              ) : (
                <>
                  {t.login.submit}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
